/**
 * I18n Context
 *
 * Provides react-intl IntlProvider with dynamic locale switching.
 * Loads messages from i18n/messages/{locale}.json.
 */

import { createContext, useContext, useState, useCallback, useMemo } from "react";
import { IntlProvider } from "react-intl";
import { useAuth } from "context/AuthContext";
import logger from "services/logger";

// Import all message files statically (tree-shaking friendly)
import en from "i18n/messages/en.json";
import fr from "i18n/messages/fr.json";

const MESSAGES = { en, fr };
const SUPPORTED_LOCALES = Object.keys(MESSAGES);
const DEFAULT_LOCALE = "en";

/** Locale metadata — used by language switcher UI */
const LOCALE_INFO = {
  en: { code: "en", name: "English", nativeName: "English", flag: "🇬🇧" },
  fr: { code: "fr", name: "French", nativeName: "Français", flag: "🇫🇷" },
};

const I18nContext = createContext(null);
I18nContext.displayName = "I18nContext";

export function I18nProvider({ children }) {
  const { userData } = useAuth();
  const savedLocale = userData?.language || localStorage.getItem("kadmin_locale") || DEFAULT_LOCALE;
  const [locale, setLocaleState] = useState(
    SUPPORTED_LOCALES.includes(savedLocale) ? savedLocale : DEFAULT_LOCALE
  );

  const setLocale = useCallback((newLocale) => {
    if (!SUPPORTED_LOCALES.includes(newLocale)) {
      logger.warn("[I18n]", `Locale "${newLocale}" not supported`);
      return;
    }
    setLocaleState(newLocale);
    localStorage.setItem("kadmin_locale", newLocale);
    logger.debug("[I18n]", `Locale changed to ${newLocale}`);
  }, []);

  const messages = MESSAGES[locale] || MESSAGES[DEFAULT_LOCALE];

  const value = useMemo(
    () => ({
      locale,
      setLocale,
      supportedLocales: SUPPORTED_LOCALES,
      localeInfo: LOCALE_INFO,
    }),
    [locale, setLocale]
  );

  return (
    <I18nContext.Provider value={value}>
      <IntlProvider
        locale={locale}
        messages={messages}
        defaultLocale={DEFAULT_LOCALE}
        onError={(err) => {
          if (err.code === "MISSING_TRANSLATION") {
            logger.debug("[I18n]", `Missing: ${err.message}`);
          } else {
            logger.warn("[I18n]", err.message);
          }
        }}
      >
        {children}
      </IntlProvider>
    </I18nContext.Provider>
  );
}

export function useI18n() {
  const context = useContext(I18nContext);
  if (!context) throw new Error("useI18n must be used within I18nProvider");
  return context;
}

/**
 * useT — shorthand for useIntl().formatMessage
 */
export function useT() {
  // eslint-disable-next-line
  const { useIntl } = require("react-intl");
  const intl = useIntl();
  return useCallback(
    (id, values = {}) => intl.formatMessage({ id }, values),
    [intl]
  );
}

export default I18nContext;
